import { Component, computed, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { EgRailGroup, EgRailNavItem } from '@expertgroup/shell';
import { CurrentUserService } from '@/app/core/current-user/current-user.service';
import { PERSONA_PATHS, PersonaService } from '@/app/core/current-user/persona.service';
import { I18n } from '@/app/core/i18n/i18n';
import { operatorRouteOpens } from '@/app/core/navigation/operator-routes';

interface MenuItem {
    label: string;
    icon: string;
    routerLink: string;
    visible?: boolean;
}
interface MenuGroup {
    /** Kljuc pamcenja sklopljenosti. Stabilan, jer je `label` prevod i menja se sa jezikom. */
    key: string;
    label: string;
    visible?: boolean;
    /**
     * `false` fiksira sekciju otvorenom. Podrazumevano je sklopiva, pa se postavlja
     * samo na primarnu sekciju. Vezuje se na `[collapsible]` jer sekcije dolaze iz
     * jednog @for-a nad `model()` — po sekciji se u markup-u ne moze pisati.
     */
    collapsible?: boolean;
    items: MenuItem[];
}

/**
 * Sadrzaj navigacije. Model je ostao isti; nestala je rekurzivna AppMenuitem
 * komponenta (233 linije) sa animacijama razvijanja podmenija — sina je ravna,
 * podmenija nema, i nikad ih nije ni imala.
 *
 * Sekcije su <eg-rail-group> iz ljuske 0.9.0: naslov sklopive sekcije je dugme,
 * sklopljenost se pamti u localStorage-u po `key`. Uslov vidljivosti sekcije ostaje
 * SPOLJA, u @if-u — grupa bez dozvole ne treba da postoji, a ne da postoji sklopljena.
 *
 * PRAVILO sklopivosti: sklopivo je ono sto je sekundarno ili promenljive duzine;
 * fiksno otvorena ostaje primarna sekcija — ona u kojoj je kontrolna tabla. Sklapanje
 * postoji da dugacke liste odu s puta, a ne da se primarna navigacija skriva za jedan
 * red. Zato PREGLED nosi `collapsible: false`, a OSMATRANJE / ORGANIZACIJA /
 * BEZBEDNOST / FEDERACIJA / KOMUNIKACIJA ostaju sklopive.
 *
 * Sto se u DOM-u menja kad je `collapsible` false: ljuska naslov renderuje kao
 * <div class="eg-rail__group-label">, ne kao dugme — nema strelice, nema
 * aria-expanded/aria-controls, i naslov nije fokusabilan. Uz to je `isCollapsed()`
 * trajno false, pa zapamceno stanje iz localStorage-a ne moze da zatvori sekciju.
 *
 * ⚠️ Ikone su OVDE bez `ph-fill` prefiksa. Ljuskin egRailNavItem ga sam dodaje,
 * pa bi puno ime dalo `ph-fill ph-fill ph-gauge`.
 */
@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [RouterLink, RouterLinkActive, EgRailGroup, EgRailNavItem],
    template: `
        @for (group of model(); track group.key) {
            @if (group.visible !== false) {
                <eg-rail-group [key]="group.key" [label]="group.label" [count]="group.items.length" [collapsible]="group.collapsible !== false">
                    @for (item of group.items; track item.routerLink) {
                        @if (item.visible !== false) {
                            <a egRailNavItem [routerLink]="item.routerLink" routerLinkActive="is-active" [icon]="item.icon">{{ item.label }}</a>
                        }
                    }
                </eg-rail-group>
            }
        }
    `
})
export class AppMenu {
    private readonly currentUser = inject(CurrentUserService);
    private readonly persona = inject(PersonaService);
    private readonly i18n = inject(I18n);

    /**
     * The menu of the actor's persona (T1-07, `PersonaService`), reactive over the actor and the server portfolio:
     * - operator: the pre-T1-07 menu, unchanged — an entry whose route guard would refuse the actor is not shown (T1-06);
     * - tenant administrator: Pregled (its home: the portfolio, or its one hub), Ljudi (`hasPeople`, T1-08), Moji tenanti
     *   (two tenants or more), Moj nalog. The global registries (Tenanti, Grupe, Korisnici, Aplikacije, Federacija, Entra federacija,
     *   Provajderi prijave, Ekosistem) and the standalone Lease, API klijenti and Obaveštenja screens are not offered: their function lives
     *   in the tenant hub. Hiding is not protection — the server refuses those reads on its own — it removes every link
     *   that would end on /zabranjen-pristup;
     * - user: Moje aplikacije, Moj nalog;
     * - unresolved (`Me` or the portfolio read failed): the same two pages — the only ones every persona may open, so
     *   none can be refused whatever the persona turns out to be. Not final: the next navigation retries the read and the
     *   menu follows the persona it decides (`currentUserGuard` as the shell's `canActivateChild`).
     */
    model = computed<MenuGroup[]>(() => {
        switch (this.persona.kind()) {
            case 'operator':
                return this.withoutHiddenItems(this.operatorGroups());
            case 'tenantAdmin':
                return this.withoutHiddenItems(this.tenantAdminGroups());
            case 'user':
            case 'unresolved':
                return this.withoutHiddenItems(this.userGroups());
        }
    });

    private withoutHiddenItems(groups: MenuGroup[]): MenuGroup[] {
        return groups.map((group) => ({ ...group, items: group.items.filter((item) => item.visible !== false) })).filter((group) => group.visible !== false && group.items.length > 0);
    }

    /** An operator entry is shown exactly where its route's own `requirePermission` lets the actor through. */
    private opens(path: string): boolean {
        return operatorRouteOpens(this.currentUser, path);
    }

    private readonly operatorGroups = computed<MenuGroup[]>(() => [
        {
            key: 'pregled',
            label: this.i18n.t('navigation.overviewGroup'),
            // Primarna sekcija: kontrolna tabla je pocetni ekran i ne sklapa se.
            collapsible: false,
            items: [{ label: this.i18n.t('navigation.dashboard'), icon: 'ph-gauge', routerLink: PERSONA_PATHS.dashboard }]
        },
        {
            key: 'osmatranje',
            label: this.i18n.t('navigation.observabilityGroup'),
            visible: this.opens('/ekosistem'),
            items: [{ label: this.i18n.t('navigation.ecosystem'), icon: 'ph-graph', routerLink: '/ekosistem' }]
        },
        {
            key: 'organizacija',
            label: this.i18n.t('navigation.organizationGroup'),
            items: [
                { label: this.i18n.t('navigation.tenants'), icon: 'ph-buildings', routerLink: '/tenanti', visible: this.opens('/tenanti') },
                { label: this.i18n.t('navigation.groups'), icon: 'ph-users-three', routerLink: '/grupe', visible: this.opens('/grupe') }
            ]
        },
        {
            key: 'bezbednost',
            label: this.i18n.t('navigation.securityGroup'),
            items: [
                { label: this.i18n.t('navigation.users'), icon: 'ph-users', routerLink: '/korisnici', visible: this.opens('/korisnici') },
                { label: this.i18n.t('navigation.applications'), icon: 'ph-squares-four', routerLink: '/aplikacije' },
                { label: this.i18n.t('navigation.accessAndLeases'), icon: 'ph-shield-check', routerLink: '/lease' },
                // The registry across tenants (UX pregled 4, D-UX4-1): only where its whole read is — `apiClientRegistryAvailable`.
                { label: this.i18n.t('navigation.apiClients'), icon: 'ph-key', routerLink: '/api-klijenti', visible: this.opens('/api-klijenti') }
            ]
        },
        {
            // UX pregled 5 (D-UX5-1): the three screens of trust across the boundary, each named by its direction — EG signs users in
            // to SSO applications, Entra organisations sign users in to EG, through the login providers. Every entry behind its guard.
            key: 'federacija',
            label: this.i18n.t('navigation.federationGroup'),
            items: [
                { label: this.i18n.t('navigation.ssoApplications'), icon: 'ph-share-network', routerLink: '/federacija', visible: this.opens('/federacija') },
                { label: this.i18n.t('navigation.entraOrganizations'), icon: 'ph-windows-logo', routerLink: '/entra-federacija', visible: this.opens('/entra-federacija') },
                { label: this.i18n.t('navigation.loginProviders'), icon: 'ph-sign-in', routerLink: '/provajderi-prijave', visible: this.opens('/provajderi-prijave') }
            ]
        },
        {
            key: 'komunikacija',
            label: this.i18n.t('navigation.communicationGroup'),
            items: [{ label: this.i18n.t('navigation.announcements'), icon: 'ph-megaphone', routerLink: '/obavestenja' }]
        }
    ]);

    /**
     * EXTENSION POINT (plan chapter 2: "Pregled, Ljudi, Pozivnice, Moji tenanti, Moj nalog"): the tenant administrator's
     * entries between Pregled and Moji tenanti. "Pozivnice" (T2-06) goes here when its screen exists — deliberately not
     * before: an entry without a screen would be a dead link. Each is offered only where its own route guard lets the
     * administrator through: "Ljudi" (T1-08) reads `hasPeople`, the predicate its route guard reads.
     */
    private readonly tenantAdminWorkItems = computed<MenuItem[]>(() => [{ label: this.i18n.t('navigation.people'), icon: 'ph-users', routerLink: PERSONA_PATHS.people, visible: this.persona.hasPeople() }]);

    private readonly tenantAdminGroups = computed<MenuGroup[]>(() => [
        {
            key: 'pregled',
            label: this.i18n.t('navigation.overviewGroup'),
            // The persona's only section: fixed open, like the operator's primary one.
            collapsible: false,
            items: [
                // Pregled is the persona's home: the portfolio, or — for an administrator of one tenant — that tenant's hub.
                { label: this.i18n.t('navigation.overview'), icon: 'ph-gauge', routerLink: this.persona.homeUrl() },
                ...this.tenantAdminWorkItems(),
                { label: this.i18n.t('navigation.myTenants'), icon: 'ph-buildings', routerLink: PERSONA_PATHS.myTenants, visible: this.persona.hasTenantList() },
                { label: this.i18n.t('navigation.myAccount'), icon: 'ph-user-circle', routerLink: PERSONA_PATHS.myAccount }
            ]
        }
    ]);

    private readonly userGroups = computed<MenuGroup[]>(() => [
        {
            key: 'licno',
            label: this.i18n.t('navigation.personalGroup'),
            collapsible: false,
            items: [
                { label: this.i18n.t('navigation.myApplications'), icon: 'ph-squares-four', routerLink: PERSONA_PATHS.myApplications },
                { label: this.i18n.t('navigation.myAccount'), icon: 'ph-user-circle', routerLink: PERSONA_PATHS.myAccount }
            ]
        }
    ]);
}
